import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'

// next components
import Head from 'next/head'
import Image from 'next/image'

// third party components
import { useDispatch } from 'react-redux'
import { useWallet } from '@solana/wallet-adapter-react'

// components
import MainSection from 'components/pages/ArtistProfile/MainSection'

// images and icons
import HeaderImage from 'assets/images/banner-image.png'
import AvatarImage from 'assets/images/dashboard-avatar.png'
import DefaultNftBG from 'assets/images/default-nft-bg.png'
import CloseIcon from 'assets/icons/close-screen.svg?component'

// styles
import styles from './id.module.scss'

// call apis
import artistsAPI from 'apis/artists'
import nftsAPI from 'apis/nfts'
import ArtistLayout from 'components/Layout/ArtistLayout'
import { isString } from 'utils/helpers'
import { useAuthentication } from 'hooks/useAuthentication'
import { getAbsoluteOgUrl } from 'utils/seo'
import ProfileTabSection from './profile/TabSection'
import { useViewport } from 'utils'

interface ArtistProfileProps {
  artistPublicKey?: string
  publicArtistData?: any
  error?: string
}

const ArtistProfile = ({ artistPublicKey, publicArtistData, error }: ArtistProfileProps) => {
  const router = useRouter()
  const { tab } = router.query

  useAuthentication()
  const { connected, publicKey } = useWallet()

  const dispatch = useDispatch()
  const removeCollectionId = useSelector(state => (state as any).removeCollectionId)
  const loginAndRefresh = useSelector(state => (state as any).loginAndRefresh)

  const [artistInfo, setArtistInfo] = useState<any>(publicArtistData)
  const [artistStatsInfo, setArtistStatsInfo] = useState<any>({})
  const [collectorStatsInfo, setCollectorStatsInfo] = useState<any>({})
  const [categoryList, setCategoryList] = useState<any>([])
  const [collections, setCollections] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isColletorLoaded, setIsColletorLoaded] = useState(false)
  const [userProfileUrl, setUserProfileUrl] = useState('')
  const [fullScreenMode, setFullScreenMode] = useState(false)
  const [artistCurrentPage, setArtistCurrentPage] = useState(1)
  const [artistRowCount, setArtistRowCount] = useState(12)
  const [collectorCurrentPage, setCollectorCurrentPage] = useState(1)
  const [collectorRowCount, setCollectorRowCount] = useState(12)
  const [isFetchingCreated, setIsFetchingCreated] = useState(false)
  const [isVisibleFetchingCreated, setIsVisibleFetchingCreated] = useState(false)
  const [hasLoading, setHasLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<any>(0)

  useEffect(() => {
    if (tab !== undefined) {
      setActiveTab(Number(tab))
    }
  }, [tab])

  const setFullScreenView = url => {
    window.scrollTo({
      top: 0,
    })
    document.body.style.overflow = 'hidden'
    setFullScreenMode(true)
    setUserProfileUrl(url)
  }

  const [isOwn, setIsOwn] = useState<any>(false)
  useEffect(() => {
    if (publicKey) setIsOwn(artistInfo?.wallet_id === publicKey?.toBase58())
  }, [artistInfo, publicKey])

  useEffect(() => {
    setTimeout(
      () =>
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        }),
      500
    )
  }, [])

  const resetData = useCallback(() => {
    setArtistStatsInfo({
      nfts_collected: 'N/A',
      collections_collected: 'N/A',
      unique_artists_owned: 'N/A',
      purchase_volume: 'N/A',
      nfts_created: 'N/A',
      collections_created: 'N/A',
      unique_collectors: 'N/A',
      sales_volume: 'N/A',
    })
    setCollectorStatsInfo({
      nfts_collected: 'N/A',
      collections_collected: 'N/A',
      unique_artists_owned: 'N/A',
      purchase_volume: 'N/A',
      nfts_created: 'N/A',
      collections_created: 'N/A',
      unique_collectors: 'N/A',
      sales_volume: 'N/A',
    })
    setCollections([])
    setCategoryList([])
  }, [])

  const getArtistData = useCallback((id, row_count, current_page, refresh = false, reset = false) => {
    artistsAPI.getArtist(id, row_count, current_page).then(
      response => {
        setIsLoaded(true)
        setHasLoading(false)
        setIsFetchingCreated(false)
        if (isString(response)) return
        let artist = response.artist

        if (refresh && artist) {
          artist.profile_picture ||= AvatarImage.src
          artist.banner_picture ||= HeaderImage.src
          setArtistInfo(artist)
        }
        setArtistStatsInfo(response.stats)

        setArtistCurrentPage(current_page)

        setCollections(prevCollections => {
          if (prevCollections.length > 0 && prevCollections.length === response.collections.length)
            setIsVisibleFetchingCreated(false)
          return response.collections
        })

        setCategoryList(prevCategoryList => {
          if (prevCategoryList.length === 0) return response.categories
          return prevCategoryList
        })
      },
      error => {
        setIsLoaded(true)
        setHasLoading(false)
        setIsFetchingCreated(false)
        setIsVisibleFetchingCreated(false)
      }
    )
  }, [])

  const getCollectorData = useCallback((id, row_count, current_page, refresh = false, reset = false) => {
    artistsAPI.getCollector(id, row_count, current_page).then(
      response => {
        setIsColletorLoaded(true)
        if (isString(response)) return
        setCollectorStatsInfo(response.stats)
      },
      error => {
        setIsColletorLoaded(true)
      }
    )
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!connected || !loginAndRefresh) return
      dispatch({ type: 'set', loginAndRefresh: false })
      await getArtistData(artistPublicKey, artistRowCount, artistCurrentPage, true, true)
      await getCollectorData(artistPublicKey, collectorRowCount, collectorCurrentPage, true, true)
    })()
  }, [
    connected,
    loginAndRefresh,
    publicArtistData,
    dispatch,
    artistPublicKey,
    artistRowCount,
    artistCurrentPage,
    collectorRowCount,
    collectorCurrentPage,
    getArtistData,
    getCollectorData,
  ])

  useEffect(() => {
    if (removeCollectionId.length > 0) {
      let newCollections = collections.filter(item => item.collection_id !== removeCollectionId)
      setCollections(newCollections)
      dispatch({ type: 'set', removeCollectionId: '' })
    }
  }, [removeCollectionId, collections, dispatch])

  const reloadPage = () => {
    getArtistData(artistPublicKey, artistRowCount, artistCurrentPage, false, true)
    getCollectorData(artistPublicKey, artistRowCount, artistCurrentPage, false, true)
  }

  const handleNextPage = () => {
    const nextPage = artistCurrentPage + 1
    getArtistData(artistPublicKey, artistRowCount, nextPage)
  }

  useEffect(() => {
    if (!artistPublicKey) return
    setHasLoading(true)
    resetData()
    getArtistData(artistPublicKey, artistRowCount, artistCurrentPage, true, true)
    getCollectorData(artistPublicKey, collectorRowCount, collectorCurrentPage, true, true)
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [
    artistPublicKey,
    artistRowCount,
    artistCurrentPage,
    collectorRowCount,
    collectorCurrentPage,
    getArtistData,
    getCollectorData,
    resetData,
  ])

  const [refreshing, setRefreshing] = useState(false)
  const refreshNFT = wallet => {
    setRefreshing(true)
    nftsAPI.burntNft({ user: wallet, nfts: [] }).then(
      response => {
        reloadPage()
        setTimeout(() => {
          setRefreshing(false)
        }, 3000)
      },
      error => {
        console.log(error)
        setTimeout(() => {
          setRefreshing(false)
        }, 3000)
      }
    )
  }

  let artistName = `${artistInfo?.username} | Foster Marketplace`
  let artistImageUrl = getAbsoluteOgUrl(artistInfo?.profile_picture ?? DefaultNftBG.src)
  let artistDescription = artistInfo?.bio ?? ''

  const viewport = useViewport()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [sidebarHeight, setSidebarHeight] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const prevY = useRef(0)
  const headerHeight = 80

  useEffect(() => {
    setViewportHeight(window.innerHeight)

    const updateSidebarHeight = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.scrollHeight)
        setViewportHeight(window.innerHeight)
        setIsInitialized(true)
      }
    }

    updateSidebarHeight()
    window.addEventListener('resize', updateSidebarHeight)
    return () => window.removeEventListener('resize', updateSidebarHeight)
  }, [])

  useEffect(() => {
    if (!sidebarRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setSidebarHeight(entry.contentRect.height)
        setViewportHeight(window.innerHeight)
      }
    })

    resizeObserver.observe(sidebarRef.current)

    return () => resizeObserver.disconnect()
  }, [sidebarRef])

  useEffect(() => {
    if (!isInitialized) return

    const handleScroll = () => {
      if (!sidebarRef.current) return

      const scrollY = window.scrollY
      const windowHeight = viewportHeight - headerHeight
      const sidebarOffset = sidebarHeight - windowHeight
      const containerRect = sidebarRef.current.getBoundingClientRect()
      let direction = scrollY > prevY.current ? 'down' : 'up'

      let offset = Math.min(prevY.current - scrollY, 20)

      if (sidebarHeight < windowHeight) {
        sidebarRef.current.style.top = `${headerHeight}px`
      } else {
        if (direction === 'up') {
          if (containerRect.top >= headerHeight - 20) {
            sidebarRef.current.style.top = `${headerHeight}px`
          } else {
            sidebarRef.current.style.top = `${containerRect.top + offset}px`
          }
        } else {
          if (containerRect.top * -1 >= sidebarOffset && scrollY > sidebarOffset) {
            sidebarRef.current.style.top = `-${sidebarOffset}px`
          } else {
            sidebarRef.current.style.top = `${containerRect.top + offset}px`
          }
        }
      }

      prevY.current = scrollY
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    handleScroll() // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [headerHeight, sidebarHeight, isInitialized, viewportHeight])

  const updateProfile = userInfo => {
    setHasLoading(true)
    setActiveTab(0)
    setArtistInfo(userInfo)
    setTimeout(() => {
      setHasLoading(false)
    }, 1000)
  }

  return (
    <div className="w-full min-h-screen flex justify-center">
      <Head>
        <title>{artistName}</title>
        <meta property="description" content={artistDescription} />

        <meta name="og:title" content={artistName} />
        <meta property="og:description" content={artistDescription} />
        <meta name="og:image" content={artistImageUrl} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={artistName} />
        <meta property="twitter:description" content={artistDescription} />
        <meta property="twitter:image" content={artistImageUrl} />

        <link rel="icon" href="/logo.png" />
      </Head>
      <div className="g-container w-full lg:flex gap-[40px]">
        <div
          ref={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? null : sidebarRef}
          className="w-full lg:w-[334px] lg:sticky"
          style={{ height: 'fit-content' }}
        >
          <MainSection
            artistInfo={artistInfo}
            artistStatsInfo={artistStatsInfo}
            collectorStatsInfo={collectorStatsInfo}
            categoryList={categoryList}
            isOwn={isOwn}
            isLoaded={isLoaded}
            isColletorLoaded={isColletorLoaded}
            setFullScreenView={setFullScreenView}
            wallet={artistInfo?.wallet_id}
            reloadPage={reloadPage}
            hasLoading={hasLoading}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            refreshing={refreshing}
            refreshNFT={() => refreshNFT(artistInfo?.wallet_id)}
            updateProfile={updateProfile}
          />
        </div>

        <div className="w-full lg:w-[calc(100%-330px)]">
          <ProfileTabSection
            activeTab={activeTab}
            collections={collections}
            wallet={artistInfo?.wallet_id}
            isOwn={isOwn}
            handleNextPage={() => handleNextPage()}
            reloadPage={reloadPage}
            isVisibleFetchingCreated={isVisibleFetchingCreated}
            isFetchingCreated={isFetchingCreated}
            setIsFetchingCreated={setIsFetchingCreated}
            refreshing={refreshing}
            refreshNFT={() => refreshNFT(artistInfo?.wallet_id)}
            hasLoading={hasLoading}
          />
        </div>

        {fullScreenMode && (
          <div className={styles.fullScreen}>
            <img
              src={userProfileUrl}
              alt=""
              className="w-[260px] h-[260px] lg:w-[500px] lg:h-[500px] rounded-full object-fit"
            />
            <div
              className={styles.closeScreen}
              onClick={() => {
                setFullScreenMode(false)
                document.body.style.overflow = 'unset'
              }}
            >
              <CloseIcon />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ArtistProfile

const LayoutWrapper = ({ children }: { children }) => {
  const viewport = useViewport()
  const isMobile = viewport === 'xs' || viewport === 'sm' || viewport === 'md'

  return <ArtistLayout style={{ overflow: isMobile ? 'hidden' : 'visible' }}>{children}</ArtistLayout>
}

ArtistProfile.getLayout = function getLayout(page) {
  return <LayoutWrapper>{page}</LayoutWrapper>
}

ArtistProfile.getInitialProps = async function ({ res, query }): Promise<ArtistProfileProps> {
  let artistUsername = encodeURIComponent(query.id)

  try {
    let publicArtistData = await fetch(`${process.env.BACKEND_URL}v1/users/username/${artistUsername}`).then(r =>
      r.json()
    )
    publicArtistData.profile_picture ||= AvatarImage.src
    publicArtistData.banner_picture ||= HeaderImage.src
    let artistPublicKey = publicArtistData.wallet_id
    return {
      artistPublicKey,
      publicArtistData,
    }
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not load artist details: ${artistUsername}: ${e.message}`,
    }
  }
}
